import { Node } from "@xyflow/react"
import {
  ActionTypeEnum,
  isActionConfigValid,
  isEventConfigValid,
  validateEventHasAction,
  validateWorkflowCondition,
  WorkflowEventEnum,
  WorkflowNodeSchema,
  WorkflowResponseDataSchema,
} from "schema"
import { z } from "zod"
import { CARD_HEIGHT, CARD_WIDTH } from "./constants"

type Workflow = z.infer<typeof WorkflowResponseDataSchema>

// A canvas node is a workflow node (event/condition/action) tagged with the workflow group it
// belongs to. `workflowId` is the canvas group id (a BuilderWorkflow.id), not the persisted id.
type BuilderNode = z.infer<typeof WorkflowNodeSchema> & { workflowId?: string }

// One workflow group on the canvas. `persistedId` is the saved Workflow id (absent for a newly
// added group that has not been saved yet). `isEnabled` gates whether the workflow executes.
type BuilderWorkflow = {
  id: string
  name: string | null
  isEnabled: boolean
  persistedId?: string
}

export type BuilderFormValues = {
  nodes?: BuilderNode[] | null
  workflows?: BuilderWorkflow[] | null
}

const GROUP_PADDING = 24

// Load all of a scope's workflows into one flat canvas: each workflow becomes a group (with its
// name) and every node is tagged with that group's id. Events stay as root nodes.
export function workflowsToFlat(workflows: Workflow[]): {
  workflows: BuilderWorkflow[]
  nodes: BuilderNode[]
} {
  const groups: BuilderWorkflow[] = workflows.map((workflow) => ({
    id: workflow.id,
    name: workflow.name,
    isEnabled: workflow.isEnabled,
    persistedId: workflow.id,
  }))

  const nodes: BuilderNode[] = workflows.flatMap((workflow) =>
    workflow.nodes.map((node) => {
      const base = {
        id: node.id,
        parentId: node.parentId,
        branch: node.branch,
        order: node.order ?? undefined,
      }
      if (node.event) {
        return {
          ...base,
          type: "Event",
          event: node.event.type,
          config: node.event.config,
          workflowId: workflow.id,
        }
      }
      if (node.action) {
        return {
          ...base,
          type: "Action",
          action: node.action.type,
          config: node.action.config,
          workflowId: workflow.id,
        }
      }
      return { ...base, type: "Condition", conditions: node.conditions, workflowId: workflow.id }
    }),
  )

  return { workflows: groups, nodes }
}

type WorkflowDraft = {
  persistedId?: string
  name: string | null
  isEnabled: boolean
  nodes: Omit<BuilderNode, "workflowId">[]
}

// Split the canvas back into one draft per workflow group for saving. Empty groups (no nodes) are
// dropped so they are never persisted; if one was persisted before, leaving it out of the drafts
// lets the save reconcile archive it.
export function flatToWorkflows(
  workflows: BuilderWorkflow[],
  nodes: BuilderNode[],
): WorkflowDraft[] {
  return workflows
    .map((group) => ({
      persistedId: group.persistedId,
      name: group.name,
      isEnabled: group.isEnabled,
      nodes: nodes
        .filter((node) => node.workflowId === group.id)
        .map(({ workflowId: _workflowId, ...node }) => node),
    }))
    .filter((draft) => draft.nodes.length > 0)
}

// A workflow is valid when every event has a configured trigger, a downstream action, and all of
// its nodes are complete. Gates Save so partial workflows can never be persisted.
export function isCanvasValid(workflows: BuilderWorkflow[], nodes: BuilderNode[]): boolean {
  if (workflows.length === 0) return true

  for (const group of workflows) {
    const groupNodes = nodes.filter((node) => node.workflowId === group.id)
    // Empty workflows render no box and are dropped on save, so they never gate validity.
    if (groupNodes.length === 0) continue
    const events = groupNodes.filter((node) => node.type === "Event")
    if (events.length === 0) return false

    if (!validateEventHasAction({ nodes: groupNodes })) return false

    for (const node of groupNodes) {
      if (node.type === "Event") {
        if (!node.event) return false
        if (!isEventConfigValid(node.event as keyof typeof WorkflowEventEnum, node.config ?? {})) {
          return false
        }
      }
      if (node.type === "Action") {
        if (!node.action) return false
        if (!isActionConfigValid(node.action as keyof typeof ActionTypeEnum, node.config ?? {})) {
          return false
        }
      }
      if (node.type === "Condition") {
        const conditions = node.conditions ?? []
        const allValid = conditions.every(
          (condition) =>
            !!condition.field && !!condition.operator && validateWorkflowCondition(condition),
        )
        if (conditions.length === 0 || !allValid) return false
      }
    }
  }

  return true
}

const GROUP_TOOLBAR_HEIGHT = 44
export const GROUP_BANNER_HEIGHT = 20
const GROUP_BANNER_TOP_GAP = 8

// Per workflow, derive three nodes from the laid-out content's bounding box:
//  - a background "box" (behind the cards, non-interactive),
//  - a "banner" node pinned to the box's top interior with a high zIndex; it renders a warning
//    strip when the workflow is disabled (and nothing otherwise),
//  - a "toolbar" node ABOVE the box with a high zIndex so the name + buttons sit on top of every
//    card and are clickable. All go into the controlled node state (see BuilderContext) so React
//    Flow measures them; being in-canvas, they scale with the zoom.
export function computeGroupBoxes(contentNodes: Node[], workflows: BuilderWorkflow[]): Node[] {
  return workflows.flatMap((group) => {
    const groupNodes = contentNodes.filter(
      (node) => (node.data as { workflowId?: string } | undefined)?.workflowId === group.id,
    )
    if (groupNodes.length === 0) return []

    const minX = Math.min(...groupNodes.map((n) => n.position.x))
    const minY = Math.min(...groupNodes.map((n) => n.position.y))
    const maxX = Math.max(...groupNodes.map((n) => n.position.x + CARD_WIDTH))
    const maxY = Math.max(...groupNodes.map((n) => n.position.y + CARD_HEIGHT))

    const boxWidth = maxX - minX + GROUP_PADDING * 2
    // A disabled workflow's cards are shifted down by GROUP_BANNER_HEIGHT (see BuilderContext) to
    // make room for the banner. Subtracting the same amount here keeps the box-top (and toolbar)
    // aligned with every other workflow's — the box just grows downward. Enabled workflows have no
    // header and no shift.
    const header = group.isEnabled ? 0 : GROUP_BANNER_HEIGHT
    const boxTop = minY - GROUP_PADDING - header

    const box: Node = {
      id: `group-${group.id}`,
      type: "workflowGroup",
      position: { x: minX - GROUP_PADDING, y: boxTop },
      data: { workflowId: group.id },
      draggable: false,
      selectable: false,
      zIndex: -1,
      style: {
        width: boxWidth,
        height: maxY - minY + GROUP_PADDING * 2 + header,
      },
    }

    const banner: Node = {
      id: `banner-${group.id}`,
      type: "workflowBanner",
      position: { x: minX - GROUP_PADDING + 8, y: boxTop + GROUP_BANNER_TOP_GAP },
      data: { workflowId: group.id, width: boxWidth - 16 },
      draggable: false,
      // NOTE: do not set `selectable: false` — React Flow drops pointer events on a node that is
      // both non-selectable and non-draggable, which kills the banner's tooltip.
      zIndex: 900,
    }

    const toolbar: Node = {
      id: `toolbar-${group.id}`,
      type: "workflowToolbar",
      position: { x: minX - GROUP_PADDING, y: boxTop - GROUP_TOOLBAR_HEIGHT },
      data: { workflowId: group.id, width: boxWidth },
      draggable: false,
      zIndex: 1000,
    }

    return [box, banner, toolbar]
  })
}
